// =============================================================================
// OELOX — Drivers domain helpers (restaurant roster + assignment)
// =============================================================================

export type VehicleType = "bicycle" | "motorcycle" | "car";
export type DriverOnlineStatus = "online" | "offline" | "busy";
export type DriverLinkStatus = "active" | "blocked";
export type OrderDriverStatus =
  | "pending"
  | "accepted"
  | "picked_up"
  | "delivered"
  | "cancelled";

export type DriverWalletTxType =
  | "cod_collected"
  | "digital_payment"
  | "payout"
  | "adjustment"
  | "trip_fee"
  | "cod_settled";

export interface DriverRecord {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  vehicle_type: VehicleType;
  vehicle_number: string | null;
  vehicle_model: string | null;
  vehicle_year: number | null;
  vehicle_color: string | null;
  photo_url: string | null;
  is_active: boolean;
  is_online: boolean;
  is_verified: boolean;
  driver_status: DriverOnlineStatus;
  collects_cod: boolean;
  link_status: DriverLinkStatus;
  acceptance_rate: number;
  completed_deliveries: number;
  rating: number;
  assigned_orders_count: number;
  created_at: string;
  link_created_at: string;
  wallet_balance: number;
  trips_count: number;
  cod_pending_total: number;
  trip_fees_total: number;
  /** Permanent login passcode bound to this driver (if claimed) */
  login_passcode: string | null;
}

export interface DriverStats {
  total: number;
  active: number;
  online: number;
  on_delivery: number;
}

export interface DriverPasscodeRecord {
  id: string;
  tenant_id: string;
  code: string;
  label: string | null;
  /** Restaurant-only reference: who this invite was created for */
  ref_driver_name: string | null;
  /** Restaurant-only reference: optional phone for that person */
  ref_driver_phone: string | null;
  /** Driver who claimed this code (permanent login) */
  claimed_by_driver_id: string | null;
  claimed_at: string | null;
  is_reusable: boolean;
  max_uses: number;
  use_count: number;
  expires_at: string | null;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
}

export interface DriverWalletInfo {
  id: string | null;
  balance: number;
  currency: string;
  updated_at: string;
}

export interface DriverFinanceSummary {
  cod_pending: number;
  cod_collected: number;
  trip_fees_total: number;
  active_trips: number;
  wallet_balance: number;
}

export interface DriverTripRecord {
  id: string;
  status: string;
  driver_status: OrderDriverStatus | null;
  customer_name: string;
  customer_phone: string | null;
  customer_address: string | null;
  payment_method: string;
  payment_status: string;
  subtotal: number;
  delivery_fee: number;
  total: number;
  currency: string;
  created_at: string;
  completed_at: string | null;
  updated_at: string;
}

export interface DriverLedgerEntry {
  id: string;
  order_id: string | null;
  type: DriverWalletTxType;
  amount: number;
  description: string;
  created_at: string;
}

export interface DriverDetailPayload {
  driver: DriverRecord;
  wallet: DriverWalletInfo;
  finance: DriverFinanceSummary;
  trips: DriverTripRecord[];
  ledger: DriverLedgerEntry[];
}

export interface CreateDriverInput {
  name: string;
  phone: string;
  email?: string | null;
  vehicle_type?: VehicleType;
  vehicle_number?: string | null;
  vehicle_model?: string | null;
  vehicle_year?: number | null;
  vehicle_color?: string | null;
  photo_url?: string | null;
  collects_cod?: boolean;
  reuse_existing?: boolean;
}

export interface UpdateDriverInput {
  name?: string;
  phone?: string;
  email?: string | null;
  vehicle_type?: VehicleType;
  vehicle_number?: string | null;
  vehicle_model?: string | null;
  vehicle_year?: number | null;
  vehicle_color?: string | null;
  photo_url?: string | null;
  collects_cod?: boolean;
  is_active?: boolean;
}

export interface GeneratePasscodeInput {
  /** Required restaurant reference — not sent to driver app auth */
  ref_driver_name: string;
  ref_driver_phone?: string | null;
  expires_hours?: number | null;
}

/** Restaurant returned from peek/redeem/link passcode */
export interface DriverAuthRestaurant {
  id: string;
  name: string;
  slug: string | null;
  logo_url: string | null;
  currency?: string | null;
  link_status?: DriverLinkStatus | null;
  linked_at?: string | null;
  passcode?: string | null;
}

export type PeekPasscodeStatus = "available" | "claimed";

export interface DriverAuthProfile {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  vehicle_type: VehicleType;
  vehicle_number: string | null;
  vehicle_model: string | null;
  vehicle_year: number | null;
  vehicle_color: string | null;
  photo_url: string | null;
}

export interface PeekPasscodeResult {
  valid: true;
  status: PeekPasscodeStatus;
  passcode: string;
  restaurant: DriverAuthRestaurant;
  ref_driver_name: string | null;
  ref_driver_phone: string | null;
  driver: DriverAuthProfile | null;
  link_status: DriverLinkStatus | null;
}

export interface RedeemDriverPasscodeInput {
  code: string;
  phone: string;
  name: string;
  email?: string | null;
  vehicle_type?: VehicleType;
  vehicle_number?: string | null;
  vehicle_model?: string | null;
  vehicle_year?: number | null;
  vehicle_color?: string | null;
  photo_url?: string | null;
  device_id?: string | null;
}

export interface RedeemDriverPasscodeSuccess {
  driver: DriverAuthProfile;
  link: {
    id: string;
    driver_id: string;
    tenant_id: string;
    status: DriverLinkStatus;
  };
  restaurant: DriverAuthRestaurant;
  session: {
    id: string;
    driver_id: string;
    device_id: string;
    last_active: string;
  } | null;
  passcode: string;
  reused: boolean;
  restaurants: DriverAuthRestaurant[];
}

export type RedeemDriverPasscodeResult = RedeemDriverPasscodeSuccess;
export type LoginDriverPasscodeResult = RedeemDriverPasscodeSuccess;

export interface LinkDriverRestaurantResult {
  driver: DriverAuthProfile;
  link: {
    id: string;
    driver_id: string;
    tenant_id: string;
    status: DriverLinkStatus;
  };
  restaurant: DriverAuthRestaurant;
  passcode: string;
  restaurants: DriverAuthRestaurant[];
}

export function mapAuthRestaurant(row: Record<string, unknown> | null | undefined): DriverAuthRestaurant {
  return {
    id: String(row?.id ?? ""),
    name: String(row?.name ?? "Restaurant"),
    slug: (row?.slug as string) || null,
    logo_url: (row?.logo_url as string) || null,
    currency: (row?.currency as string) || null,
    link_status: (row?.link_status as DriverLinkStatus) || null,
    linked_at: (row?.linked_at as string) || null,
    passcode: (row?.passcode as string) || null,
  };
}

export function mapAuthRestaurantList(raw: unknown): DriverAuthRestaurant[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((row) => mapAuthRestaurant(row as Record<string, unknown>));
}

export function mapAuthDriverProfile(row: Record<string, unknown> | null | undefined): DriverAuthProfile {
  return {
    id: String(row?.id ?? ""),
    name: (row?.name as string) || null,
    phone: (row?.phone as string) || null,
    email: (row?.email as string) || null,
    vehicle_type: ((row?.vehicle_type as VehicleType) || "motorcycle") as VehicleType,
    vehicle_number:
      (row?.vehicle_number as string) || (row?.vehicle_plate as string) || null,
    vehicle_model: (row?.vehicle_model as string) || null,
    vehicle_year: row?.vehicle_year != null ? Number(row.vehicle_year) : null,
    vehicle_color: (row?.vehicle_color as string) || null,
    photo_url: (row?.photo_url as string) || null,
  };
}

export function mapDriverRow(row: Record<string, unknown>): DriverRecord {
  return {
    id: String(row.id),
    name: (row.name as string) || null,
    phone: (row.phone as string) || null,
    email: (row.email as string) || null,
    vehicle_type: ((row.vehicle_type as VehicleType) || "motorcycle") as VehicleType,
    vehicle_number:
      (row.vehicle_number_display as string) ||
      (row.vehicle_number as string) ||
      null,
    vehicle_model: (row.vehicle_model as string) || null,
    vehicle_year: row.vehicle_year != null ? Number(row.vehicle_year) : null,
    vehicle_color: (row.vehicle_color as string) || null,
    photo_url: (row.photo_url as string) || null,
    is_active: Boolean(row.is_active ?? true),
    is_online: Boolean(row.is_online ?? false),
    is_verified: Boolean(row.is_verified ?? false),
    driver_status: ((row.driver_status as DriverOnlineStatus) || "offline") as DriverOnlineStatus,
    collects_cod: Boolean(row.collects_cod ?? false),
    link_status: ((row.link_status as DriverLinkStatus) || "active") as DriverLinkStatus,
    acceptance_rate: Number(row.acceptance_rate ?? 0),
    completed_deliveries: Number(row.completed_deliveries ?? 0),
    rating: Number(row.rating ?? 5),
    assigned_orders_count: Number(row.assigned_orders_count ?? 0),
    created_at: String(row.created_at ?? new Date().toISOString()),
    link_created_at: String(row.link_created_at ?? row.created_at ?? new Date().toISOString()),
    wallet_balance: Number(row.wallet_balance ?? 0),
    trips_count: Number(row.trips_count ?? 0),
    cod_pending_total: Number(row.cod_pending_total ?? 0),
    trip_fees_total: Number(row.trip_fees_total ?? 0),
    login_passcode: (row.login_passcode as string) || null,
  };
}

export function mapPasscodeRow(row: Record<string, unknown>): DriverPasscodeRecord {
  const label = (row.label as string) || null;
  let refName = (row.ref_driver_name as string) || null;
  let refPhone = (row.ref_driver_phone as string) || null;

  // Fallback when only label exists (pre-021 DBs): "Name · Phone"
  if (!refName && label) {
    const parts = label.split(" · ");
    refName = parts[0]?.trim() || label;
    if (!refPhone && parts.length > 1) refPhone = parts.slice(1).join(" · ").trim() || null;
  }

  return {
    id: String(row.id),
    tenant_id: String(row.tenant_id),
    code: String(row.code),
    label,
    ref_driver_name: refName,
    ref_driver_phone: refPhone,
    claimed_by_driver_id: (row.claimed_by_driver_id as string) || null,
    claimed_at: (row.claimed_at as string) || null,
    is_reusable: Boolean(row.is_reusable ?? false),
    max_uses: Number(row.max_uses ?? 1),
    use_count: Number(row.use_count ?? 0),
    expires_at: (row.expires_at as string) || null,
    is_active: Boolean(row.is_active ?? true),
    created_by: (row.created_by as string) || null,
    created_at: String(row.created_at ?? new Date().toISOString()),
  };
}

export function mapDriverTrip(row: Record<string, unknown>): DriverTripRecord {
  return {
    id: String(row.id),
    status: String(row.status ?? ""),
    driver_status: (row.driver_status as OrderDriverStatus) || null,
    customer_name: String(row.customer_name ?? "Guest"),
    customer_phone: (row.customer_phone as string) || null,
    customer_address: (row.customer_address as string) || null,
    payment_method: String(row.payment_method ?? "cod"),
    payment_status: String(row.payment_status ?? "unpaid"),
    subtotal: Number(row.subtotal ?? 0),
    delivery_fee: Number(row.delivery_fee ?? 0),
    total: Number(row.total ?? 0),
    currency: String(row.currency ?? "USD"),
    created_at: String(row.created_at ?? new Date().toISOString()),
    completed_at: (row.completed_at as string) || null,
    updated_at: String(row.updated_at ?? row.created_at ?? new Date().toISOString()),
  };
}

export function mapDriverLedger(row: Record<string, unknown>): DriverLedgerEntry {
  return {
    id: String(row.id),
    order_id: (row.order_id as string) || null,
    type: (row.type as DriverWalletTxType) || "adjustment",
    amount: Number(row.amount ?? 0),
    description: String(row.description ?? ""),
    created_at: String(row.created_at ?? new Date().toISOString()),
  };
}

export function validateDriverForm(input: {
  name: string;
  phone: string;
  email?: string;
}): string | null {
  if (!input.name.trim()) return "name_required";
  if (!/^\+?[\d\s\-()]{7,20}$/.test(input.phone.trim())) return "phone_invalid";
  if (input.email?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.email.trim())) {
    return "email_invalid";
  }
  return null;
}

export function driverDisplayName(driver: Pick<DriverRecord, "name" | "phone">): string {
  return driver.name?.trim() || driver.phone?.trim() || "Driver";
}

export function getDriverInitials(name: string | null | undefined): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();
}

export function driverVehicleLabel(driver: Pick<
  DriverRecord,
  "vehicle_number" | "vehicle_model" | "vehicle_type" | "vehicle_color"
>): string {
  const parts = [
    driver.vehicle_model,
    driver.vehicle_number,
    driver.vehicle_color,
  ].filter(Boolean);
  if (parts.length) return parts.join(" · ");
  return driver.vehicle_type || "motorcycle";
}

export function isDriverAvailableForAssign(driver: DriverRecord): boolean {
  return driver.link_status === "active" && driver.is_active;
}

export function orderDriverStatusLabel(status: OrderDriverStatus | null | undefined): string {
  const labels: Record<OrderDriverStatus, string> = {
    pending: "Waiting",
    accepted: "Accepted",
    picked_up: "Picked up",
    delivered: "Delivered",
    cancelled: "Cancelled",
  };
  if (!status) return "Unassigned";
  return labels[status] ?? status;
}

export function driverListStatus(driver: DriverRecord): "online" | "offline" | "disabled" {
  if (driver.link_status === "blocked" || !driver.is_active) return "disabled";
  // Presence comes from list/detail RPC (session heartbeat within ~3 minutes)
  if (driver.is_online) return "online";
  return "offline";
}
